import dynamic from 'next/dynamic';

import { cn } from '@natu/utils/cn';

import { StoryblokRichTextData } from '../../../../richtext';
import {
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
} from '../../../../utils';

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

const StoryblokRichtext = dynamic(() =>
  import('../../../../richtext').then(mod => mod.StoryblokRichtext),
);

const Author = dynamic(() => import('@natu/ui/Author').then(mod => mod.Author));

interface SBQuoteProps {
  content: StoryblokRichTextData;
  icon?: 'quote' | 'lightbulb';
  author?: string;
  position?: string;
  portrait?: StoryblokAsset;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
}

export const SBQuote = ({ blok }: SBProps<SBQuoteProps>) => {
  const {
    content,
    icon,
    author,
    position,
    portrait,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
  } = blok;

  if (!content && !author && !position && !portrait) return null;

  const className = resolveStoryblokStyles({
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    mt: mtMobile,
    mtTablet,
    mtDesktop,
  });

  const resolvedPortrait = getAssetFromStoryblok(portrait);

  return (
    <div className={cn(className, 'flex h-full gap-x-8 md:gap-x-10')} {...sbEditable(blok)}>
      <div className="bg-primary-600 w-0.5 shrink-0" />
      {content && icon && (
        <DynamicIcon className="text-primary-600 hidden size-16 shrink-0 md:block" icon={icon} />
      )}
      <div>
        <div className="flex flex-col gap-y-8">
          <div className="flex flex-col gap-y-6 md:block">
            {icon && (
              <DynamicIcon className="text-primary-600 size-16 shrink-0 md:hidden" icon={icon} />
            )}
            {content && <StoryblokRichtext className="text-md md:text-lg" data={content} />}
          </div>
          {(author || position || resolvedPortrait.src) && (
            <Author image={resolvedPortrait} name={author} role={position} variant="quote" />
          )}
        </div>
      </div>
    </div>
  );
};
